import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import axios from 'axios';
import showToast from '../utils/toastHelper';

const Dashboard = () => {
  const { user } = useUser();
  const [stats, setStats] = useState({
    totalOrganizations: 0,
    activeElections: 0,
    myVotes: 0,
  });
  const [organizations, setOrganizations] = useState([]);
  const [filteredOrganizations, setFilteredOrganizations] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [errorCount, setErrorCount] = useState(0); // eslint-disable-line no-unused-vars

  useEffect(() => {
    fetchDashboardData();
    
    // Auto-refresh every 15 seconds for real-time stats
    const interval = setInterval(() => {
      fetchDashboardData();
    }, 15000);
    
    // Cleanup on unmount
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Get organizations available for voting
      const orgsRes = await axios.get(`${process.env.REACT_APP_API_URL}/organizations/available-for-voting`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      // Get voter's votes
      const votesRes = await axios.get(`${process.env.REACT_APP_API_URL}/votes/my-votes`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const orgs = orgsRes.data.data || [];
      const votes = votesRes.data.data || [];
      
      // Count active elections
      const activeElectionsCount = orgs.reduce((sum, org) => sum + (org.activeElectionsCount || 0), 0);

      // Force new object references to trigger re-render
      setOrganizations([...orgs]);
      setFilteredOrganizations([...orgs]);
      setStats({
        totalOrganizations: orgs.length,
        activeElections: activeElectionsCount,
        myVotes: votes.length,
      });
      setErrorCount(0); // Reset error count on success
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      // Only show error on initial load or after 3 consecutive failures
      if (loading) {
        showToast('Failed to load dashboard data', 'error');
      } else {
        setErrorCount(prev => {
          const newCount = prev + 1;
          if (newCount >= 3) {
            showToast('Unable to refresh data. Please check your connection.', 'error');
            return 0; // Reset after showing error
          }
          return newCount;
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setFilteredOrganizations(organizations);
      return;
    }
    const filtered = organizations.filter(org => 
      org.name.toLowerCase().includes(query.toLowerCase()) ||
      org.description?.toLowerCase().includes(query.toLowerCase())
    );
    setFilteredOrganizations(filtered);
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <h2 className="mb-4">Welcome, {user?.fullName}!</h2>

      {/* Stats Cards */}
      <div className="row mb-4">
        <div className="col-md-4 mb-3">
          <div className="stats-card">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h6 className="text-muted mb-1">My Organizations</h6>
                <h3 className="mb-0">{stats.totalOrganizations}</h3>
              </div>
              <div className="stats-card-icon bg-primary bg-opacity-10 text-primary">
                <i className="bi bi-building"></i>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-4 mb-3">
          <div className="stats-card">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h6 className="text-muted mb-1">Active Elections</h6>
                <h3 className="mb-0">{stats.activeElections}</h3>
              </div>
              <div className="stats-card-icon bg-success bg-opacity-10 text-success">
                <i className="bi bi-check-circle"></i>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-4 mb-3">
          <div className="stats-card">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h6 className="text-muted mb-1">My Votes</h6>
                <h3 className="mb-0">{stats.myVotes}</h3>
              </div>
              <div className="stats-card-icon bg-info bg-opacity-10 text-info">
                <i className="bi bi-check2-square"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Organizations */}
      <div className="card mt-4">
        <div className="card-header">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h5 className="mb-0">Organizations</h5>
              <p className="mb-0" style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                Browse and participate in elections
              </p>
            </div>
            {organizations.length > 0 && (
              <div className="position-relative" style={{ width: '350px' }}>
                <i className="bi bi-search position-absolute" style={{ left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6b7280', zIndex: 10 }}></i>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search organizations..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  style={{
                    paddingLeft: '2.5rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px'
                  }}
                />
              </div>
            )}
          </div>
        </div>
        <div className="card-body">
          {organizations.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-building" style={{ fontSize: '3rem', color: '#d1d5db' }}></i>
              <p className="mt-3 text-muted">No organizations with active elections available at the moment.</p>
            </div>
          ) : filteredOrganizations.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-search" style={{ fontSize: '3rem', color: '#d1d5db' }}></i>
              <p className="mt-3 text-muted">No organizations found matching "{searchQuery}"</p>
              <button onClick={() => handleSearch('')} className="btn btn-sm btn-outline-primary mt-2">
                Clear search
              </button>
            </div>
          ) : (
            <div className="row">
              {filteredOrganizations.map((org) => (
                <div key={org.id} className="col-12 col-md-6 col-lg-4 mb-4">
                  <Link to={`/voter/organizations/${org.id}/elections`} style={{ textDecoration: 'none', color: 'inherit' }}>
                    <div className="card h-100" style={{ transition: 'transform 0.2s', cursor: 'pointer' }}
                         onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
                         onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
                      <div className="card-body">
                      <div className="d-flex align-items-center mb-3">
                        <div style={{
                          width: '50px',
                          height: '50px',
                          borderRadius: '12px',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '1.25rem',
                          fontWeight: '600',
                          marginRight: '0.75rem',
                          flexShrink: 0
                        }}>
                          {org.name?.charAt(0) || 'O'}
                        </div>
                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                          <h5 className="mb-1" style={{ fontWeight: '600', fontSize: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{org.name}</h5>
                          {org.isUserOrganization && (
                            <span className="badge" style={{ 
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              fontSize: '0.7rem' 
                            }}>
                              Your Organization
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {org.description && (
                        <p className="mb-3" style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                          {org.description.length > 120 
                            ? `${org.description.substring(0, 120)}...` 
                            : org.description}
                        </p>
                      )}
                      
                      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2 mt-3 pt-3" style={{ borderTop: '1px solid #e5e7eb' }}>
                        <div className="text-muted" style={{ fontSize: '0.813rem' }}>
                          <i className="bi bi-calendar-event me-2"></i>
                          {org.activeElectionsCount || 0} Active Election{org.activeElectionsCount !== 1 ? 's' : ''}
                        </div>
                        <span
                          className="btn btn-sm"
                          style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            border: 'none',
                            padding: '0.375rem 1rem',
                            fontSize: '0.813rem',
                            fontWeight: '500',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          View Elections
                        </span>
                      </div>
                    </div>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
